import { useState, useEffect, useCallback } from 'react';

interface MobileNativeFeatures {
  // Haptic feedback
  vibrate: (pattern?: number | number[]) => void;
  
  // Screen orientation
  lockOrientation: (orientation: 'portrait' | 'landscape') => Promise<void>;
  unlockOrientation: () => void;
  
  // Full screen
  enterFullscreen: () => Promise<void>;
  exitFullscreen: () => Promise<void>;
  isFullscreen: boolean;
  
  // Device info
  isStandalone: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  
  // Network status
  isOnline: boolean;
  
  // Safe areas
  safeAreaInsets: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  
  // Installation
  canInstall: boolean;
  promptInstall: () => Promise<void>;
  
  // Share API
  canShare: boolean;
  share: (data: ShareData) => Promise<void>;
}

export const useMobileNative = (): MobileNativeFeatures => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [canInstall, setCanInstall] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [safeAreaInsets, setSafeAreaInsets] = useState({
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  });

  // Device detection
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                      (window.navigator as any).standalone === true;

  // Haptic feedback
  const vibrate = useCallback((pattern: number | number[] = 50) => {
    if ('vibrate' in navigator) {
      navigator.vibrate(pattern);
    }
  }, []);

  // Screen orientation
  const lockOrientation = useCallback(async (orientation: 'portrait' | 'landscape') => {
    if ('screen' in window && 'orientation' in window.screen && 'lock' in window.screen.orientation) {
      try {
        await (window.screen.orientation as any).lock(orientation);
      } catch (error) {
        console.warn('Screen orientation lock failed:', error);
      }
    }
  }, []);

  const unlockOrientation = useCallback(() => {
    if ('screen' in window && 'orientation' in window.screen && 'unlock' in window.screen.orientation) {
      try {
        (window.screen.orientation as any).unlock();
      } catch (error) {
        console.warn('Screen orientation unlock failed:', error);
      }
    }
  }, []);

  // Full screen
  const enterFullscreen = useCallback(async () => {
    if (document.documentElement.requestFullscreen) {
      try {
        await document.documentElement.requestFullscreen();
      } catch (error) {
        console.warn('Fullscreen request failed:', error);
      }
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    if (document.exitFullscreen) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.warn('Exit fullscreen failed:', error);
      }
    }
  }, []);

  // Installation
  const promptInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`User ${outcome} the install prompt`);
      setDeferredPrompt(null);
      setCanInstall(false);
    }
  }, [deferredPrompt]);

  // Share API
  const canShare = 'share' in navigator;
  
  const share = useCallback(async (data: ShareData) => {
    if (canShare) {
      try {
        await navigator.share(data);
      } catch (error) {
        console.warn('Share failed:', error);
        // Fallback to clipboard
        if (data.url && 'clipboard' in navigator) {
          await navigator.clipboard.writeText(data.url);
        }
      }
    }
  }, [canShare]);

  // Calculate safe area insets
  const updateSafeAreaInsets = useCallback(() => {
    const computedStyle = getComputedStyle(document.documentElement);
    setSafeAreaInsets({
      top: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-top)') || '0', 10),
      bottom: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-bottom)') || '0', 10),
      left: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-left)') || '0', 10),
      right: parseInt(computedStyle.getPropertyValue('env(safe-area-inset-right)') || '0', 10),
    });
  }, []);

  useEffect(() => {
    // Fullscreen change listener
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    // Online/offline listeners
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    // PWA install prompt listener
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setCanInstall(true);
    };

    // App installed listener
    const handleAppInstalled = () => {
      setCanInstall(false);
      setDeferredPrompt(null);
    };

    // Add event listeners
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // Initial safe area calculation
    updateSafeAreaInsets();
    window.addEventListener('resize', updateSafeAreaInsets);
    window.addEventListener('orientationchange', updateSafeAreaInsets);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('resize', updateSafeAreaInsets);
      window.removeEventListener('orientationchange', updateSafeAreaInsets);
    };
  }, [updateSafeAreaInsets]);

  return {
    vibrate,
    lockOrientation,
    unlockOrientation,
    enterFullscreen,
    exitFullscreen,
    isFullscreen,
    isStandalone,
    isIOS,
    isAndroid,
    isOnline,
    safeAreaInsets,
    canInstall,
    promptInstall,
    canShare,
    share,
  };
};


